import   React        ,
       { useState     }  from 'react';

import   classNames      from 'classnames';

import { Check        ,
         ChevronDown  ,
         ChevronRight ,
         Ban          }  from 'lucide-react';

import   styles          from './ChatModeration.module.scss';

import { ChatModChannel         ,
         ChatModerationLevel    ,
         CHAT_MODERATION_LEVELS } from '../../../dataTypes/moderationManagment/chat_types';

import { shortDuration ,
         secondsSince  }  from './chatModerationUi';

/**
 * Les chaînes du créateur : on coche celles dont on suit le chat (autant qu'on
 * veut, leurs messages se mêlent dans une seule file), et chacune garde ses
 * propres réglages — chat ouvert ou fermé, niveau de modération.
 */

interface Props {
  channels       : ChatModChannel[];
  selectedIds    : number[];
  colorOf        : (id: number) => string;
  now            : number;
  onToggle       : (id: number) => void;
  onSelectAll    : (ids: number[]) => void;
  onUpdate       : (id: number, change: { chatOpen?: boolean; level?: ChatModerationLevel }) => void;
  onOpenBans     : () => void;
}

const ChannelRail: React.FC<Props> = ({ channels, selectedIds, colorOf, now, onToggle, onSelectAll, onUpdate, onOpenBans }) => {

  const [ openSettings, setOpenSettings ] = useState<number | null>(null);

  const allIds      = channels.map(c => c.id);
  const allSelected = channels.length > 0 && selectedIds.length === channels.length;

  return (
    <section className={classNames(styles.panel, styles.rail)} aria-label="Chaînes suivies">

      <header className={styles.panelHead}>
        <span className={styles.panelTitle}>Chaînes</span>
        <span className={classNames(styles.muted)} style={{ fontSize: 13 }}>{selectedIds.length} suivie{selectedIds.length > 1 ? 's' : ''}</span>
        <span className={styles.spacer} />
        {channels.length > 1 && (
          <button type="button" className={classNames(styles.btn, styles.btnSmall, styles.btnGhost)}
                  onClick={() => onSelectAll(allSelected ? [] : allIds)}>
            {allSelected ? 'Aucune' : 'Toutes'}
          </button>
        )}
      </header>

      <div className={styles.railList}>
        {channels.map(channel => {
          const selected = selectedIds.includes(channel.id);
          const level    = CHAT_MODERATION_LEVELS[channel.level] ?? CHAT_MODERATION_LEVELS[1];
          const aiShare  = channel.totalHour > 0 ? Math.round(channel.byAiHour * 100 / channel.totalHour) : null;
          const wait     = channel.oldestPending ? secondsSince(channel.oldestPending, now) : 0;
          const showSettings = openSettings === channel.id;

          return (
            <div key={channel.id}
                 className={classNames(styles.channel, selected && styles.channelOn)}
                 style={{ ['--chan' as any]: colorOf(channel.id) }}>

              <button type="button" className={styles.channelRow} onClick={() => onToggle(channel.id)}
                      aria-pressed={selected} title={selected ? 'Ne plus suivre ce chat' : 'Suivre ce chat'}>
                <span className={classNames(styles.check, selected && styles.checkOn)}>
                  {selected && <Check size={12} strokeWidth={3} />}
                </span>
                <span className={styles.channelName}>
                  <span className={styles.swatch} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{channel.name || `Chaîne ${channel.id}`}</span>
                  {channel.active && <span className={styles.live}>DIRECT</span>}
                </span>
                <span className={classNames(styles.count, channel.pending === 0 && styles.countZero)}
                      title={channel.pending > 0 ? `${channel.pending} à valider, le plus ancien depuis ${shortDuration(wait)}` : 'Rien à valider'}>
                  {channel.pending}
                </span>
              </button>

              <div className={styles.channelMeta}>
                {!channel.chatOpen
                  ? <span className={styles.closedNote}>Chat fermé : les spectateurs ne peuvent pas écrire</span>
                  : <span className={styles.muted}>
                      {channel.present} dans le salon · {channel.totalHour} msg/h
                      {aiShare !== null && <> · {aiShare} % tranchés par l'IA</>}
                      <br />Modération {level.label.toLowerCase()}
                    </span>}
              </div>

              <button type="button" className={styles.settingsToggle}
                      onClick={() => setOpenSettings(showSettings ? null : channel.id)}
                      aria-expanded={showSettings}>
                {showSettings ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                Réglages du chat
              </button>

              {showSettings && (
                <div className={styles.channelSettings}>
                  <div className={styles.switchRow}>
                    <span>Chat ouvert</span>
                    <button type="button" role="switch" aria-checked={channel.chatOpen}
                            aria-label={`Chat ouvert sur ${channel.name}`}
                            className={classNames(styles.switch, channel.chatOpen && styles.switchOn)}
                            onClick={() => onUpdate(channel.id, { chatOpen: !channel.chatOpen })} />
                  </div>

                  <div className={styles.levels} role="radiogroup" aria-label="Niveau de modération">
                    {CHAT_MODERATION_LEVELS.map(option => (
                      <button key={option.value} type="button" role="radio"
                              aria-checked={channel.level === option.value}
                              className={classNames(styles.level, channel.level === option.value && styles.levelOn)}
                              onClick={() => onUpdate(channel.id, { level: option.value })}>
                        {option.label}
                      </button>
                    ))}
                  </div>

                  <div className={styles.levelHint}>
                    {level.hint}.
                    {level.publish !== null && level.reject !== null && (
                      <> Publié sous {Math.round(level.publish * 100)}, refusé dès {Math.round(level.reject * 100)}, entre les deux : à vous.</>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <footer className={styles.railFoot}>
        <button type="button" className={classNames(styles.btn, styles.btnGhost)} onClick={onOpenBans} style={{ flex: 1, justifyContent: 'center' }}>
          <Ban size={15} /> Spectateurs bannis
        </button>
      </footer>
    </section>
  );
};

export default ChannelRail;
